import { useState } from "react";
import FileUploadField from "@/app/shared/file-upload-field";
import FormGroup from "@/app/shared/form-group";
import FormFooter from "@/components/form-footer";
import { EndPoints, httpService, PaginatedResult } from "@/Core/Client";
import {
    VenueFormDto,
    venueFormSchema,
    VenueResponseDto,
} from "@/Dtos/venue-dto";
import routes from "@/routers/routes";
import QuillEditor from "@/ui/quill-editor";
import cn from "@/utils/class-names";
import { zodResolver } from "@hookform/resolvers/zod";
import axios from "axios";
import { Controller, SubmitHandler, useWatch, useForm, useFieldArray } from "react-hook-form";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { ActionIcon, Button, Input, Select, Switch, Text } from "rizzui";
import { defaultValues, defaultExtraValues } from "./utils";
import { FiDollarSign } from "react-icons/fi";
import { PiPlusBold } from "react-icons/pi";
import { useQuery } from "@tanstack/react-query";
import TrashIcon from "@/components/icons/trash";
import { EntityExtraDto } from "@/Dtos/entity-dto";
import instanceAxios from "@/Core/Client/AxiosConfig";

export const priceTypeOptions = [
    {
        value: "fixed",
        label: "Fixed Price",
    },
    {
        value: "perHead",
        label: "Per Head",
    },
];

export default function VenueInfoForm() {
    const params = useParams();

    const { data: venue, isLoading: isLoadingVenue, refetch } = useQuery<VenueResponseDto | null>({
        queryKey: ["venue"],
        queryFn: async () => {
            if (!params.id) {
                return Promise.resolve<VenueResponseDto | null>(null);
            }

            const res = await instanceAxios
                .get<VenueResponseDto>(EndPoints.venues.details(params.id));

            if (res.data) {
                for (const extra of res.data.extras) {
                    append(defaultExtraValues(extra));
                }
            }

            return res.data;
        },
    });

    const {
        handleSubmit,
        register,
        control,
        formState: { errors },
    } = useForm<VenueFormDto>({
        resolver: zodResolver(venueFormSchema),
        defaultValues: defaultValues(venue ?? undefined),
    });

    const priceType = useWatch({
        control,
        name: "priceType",
    });

    const extraPriceIncluded = useWatch({
        control,
        name: "priceIncluded",
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "extras",
    });

    const [isLoading, setLoading] = useState(false);
    const navigate = useNavigate();

    const { data: extras, isLoading: isLoadingExtras } = useQuery<PaginatedResult<EntityExtraDto>>({
        queryKey: ["extras"],
        queryFn: () => instanceAxios
            .get<PaginatedResult<EntityExtraDto>>(EndPoints.extras.crud)
            .then((res) => res.data),
    });

    const onSubmit: SubmitHandler<VenueFormDto> = async (data) => {
        console.log("Venue Data", data);

        const formData = new FormData();
        formData.append("name", data.name);
        formData.append("capacity", data.capacity);
        formData.append("priceType", data.priceType);
        formData.append("price", data.price);
        formData.append("priceIncluded", data.priceIncluded?.toString() ?? "false");
        formData.append("address", data.address);
        formData.append("remarks", data.remarks);

        if (data.extras) {
            data.extras.forEach((extra, index) => {
                const entityExtra = extras!.items!.find(e => e.id === extra.name);
                formData.append(`extras[${index}][name]`, entityExtra?.name ?? "");
                formData.append(`extras[${index}][entityExtraId]`, entityExtra?.id ?? "");
                formData.append(`extras[${index}][price]`, extra.price);
                formData.append(`extras[${index}][description]`, extra.description);
                if (data.priceIncluded) {
                    formData.append(`extras[${index}][isRequired]`, "true");
                    formData.append(`extras[${index}][priceIncluded]`, "true");
                } else {
                    formData.append(`extras[${index}][isRequired]`, "false");
                    formData.append(`extras[${index}][priceIncluded]`, "false");
                }
            });
        }

        (data.images as File[]).forEach((file, index) => {
            formData.append(`images[${index}]`, file, file.name);
        });

        setLoading(true);

        try {
            let result: VenueResponseDto | null = null;

            if (venue) {
                const response = await httpService(EndPoints.venues.crud).update(venue.id, formData);
                result = response.data ?? venue;
            } else {
                const response = await httpService(EndPoints.venues.crud).post(formData);
                result = response.data;
            }

            toast.success(
                <Text as="b">
                    Venue successfully {venue ? "updated" : "created"}
                </Text>,
            );

            navigate(routes.venues.index);
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const { response } = error;
                if (response?.data?.message) {
                    toast.error(<Text as="b">{response?.data?.message}</Text>);
                }
            }
        }

        setLoading(false);
    };

    return (
        <div className="@container">
            <div className="mb-10 grid gap-7 divide-y divide-dashed divide-gray-200 @2xl:gap-9 @3xl:gap-11">
                <div className="pt-6 @2xl:pt-7 @3xl:pt-8">
                    <form
                        onSubmit={handleSubmit(onSubmit)}
                        className={cn(
                            "relative z-[19] [&_label.block>span]:font-medium",
                        )}
                    >
                        <FormGroup
                            title="Create Venue"
                            description="Edit your venue description and necessary information from here"
                            className="mb-6"
                        >
                            <div className="col-span-full grid grid-cols-3 grid-flow-col gap-4 items-end">
                                <Input
                                    label="Title"
                                    placeholder="Venue title"
                                    {...register("name")}
                                    defaultValue={venue?.name}
                                    error={errors.name?.message as string}
                                />
                                <Input
                                    label="Capacity"
                                    placeholder="Capacity"
                                    {...register("capacity")}
                                    defaultValue={venue?.capacity}
                                    error={errors.capacity?.message as string}
                                />
                                <Input
                                    label="Address"
                                    placeholder="Venue Location"
                                    {...register("address")}
                                    defaultValue={venue?.address}
                                    error={errors.address?.message as string}
                                />
                            </div>

                            <div className="col-span-full grid grid-cols-3 grid-flow-col gap-4 items-end">
                                <Controller
                                    name="priceType"
                                    control={control}
                                    defaultValue={venue?.priceType}
                                    render={({ field: { onChange, value } }) => (
                                        <Select
                                            dropdownClassName="h-auto"
                                            options={priceTypeOptions}
                                            value={value}
                                            onChange={onChange}
                                            label="Price Type"
                                            error={errors?.priceType?.message as string}
                                            getOptionValue={(option) => option.value}
                                            displayValue={(selected) =>
                                                priceTypeOptions.find(
                                                    (o) => o.value === selected,
                                                )?.label
                                            }
                                        />
                                    )}
                                />

                                <Input
                                    label="Price"
                                    placeholder="Booking Price"
                                    {...register("price")}
                                    defaultValue={venue?.price}
                                    error={errors.price?.message as string}
                                    prefix={<FiDollarSign />}
                                    suffix={priceType === "perHead" ? "Per head" : "Fixed"}
                                />

                                <Controller
                                    name="priceIncluded"
                                    control={control}
                                    defaultValue={venue?.priceIncluded}
                                    render={({ field: { onChange, value } }) => (
                                        <Switch
                                            size="md"
                                            defaultChecked={value}
                                            onChange={onChange}
                                            label="Price Includes Venue extras?"
                                            error={errors?.priceIncluded?.message as string}
                                        />
                                    )}
                                />
                            </div>

                            <Controller
                                control={control}
                                name="remarks"
                                defaultValue={venue?.remarks}
                                render={({ field: { onChange, value } }) => (
                                    <QuillEditor
                                        value={value}
                                        onChange={onChange}
                                        label="Description"
                                        className="col-span-full [&_.ql-editor]:min-h-[100px]"
                                        labelClassName="font-medium text-gray-700 dark:text-gray-600 mb-1.5"
                                    />
                                )}
                            />

                            <Controller
                                {...register("images")}
                                control={control}
                                render={({
                                    field: { onChange, onBlur },
                                    fieldState: { error },
                                }) => (
                                    <FileUploadField
                                        accept="img"
                                        name="images"
                                        className="col-span-full"
                                        onChange={(e: any) => onChange([...e.target.files])}
                                        onBlur={onBlur}
                                        error={error?.message}
                                        label="Upload venue images"
                                    />
                                )}
                            />
                        </FormGroup>

                        <FormGroup title="Venue Extras"
                            description="Add your venue extras here">
                            {fields.map((item, index) => (
                                <div key={item.id} className="col-span-full grid grid-cols-3 grid-flow-col gap-4 items-end">
                                    <Controller
                                        name={`extras.${index}.name`}
                                        control={control}
                                        render={({ field: { onChange, value } }) => (
                                            <Select
                                                options={extras?.items.map((extra) => ({ label: extra.name, value: extra.id })) ?? []}
                                                value={value}
                                                onChange={onChange}
                                                label="Extra Name"
                                                getOptionValue={(option) => option.value}
                                                displayValue={(selected) =>
                                                    extras?.items.find(o => o.id === selected)?.name
                                                }
                                            />
                                        )}
                                    />
                                    <Input type="number" label="Price" placeholder="10.00"
                                        step="0.01" defaultValue={extraPriceIncluded ? "0" : ""}
                                        readOnly={extraPriceIncluded}
                                        prefix={"$"} {...register(`extras.${index}.price`)} />
                                    <Input label="Description" placeholder="Description"
                                        labelClassName="font-medium text-gray-700 dark:text-gray-600 mb-1.5"
                                        {...register(`extras.${index}.description`)} />

                                    {fields.length > 1 && (
                                        <ActionIcon onClick={() => remove(index)}
                                            variant="flat" className="mt-7 shrink-0">
                                            <TrashIcon className="h-4 w-4" />
                                        </ActionIcon>
                                    )}
                                </div>
                            ))}
                            <Button
                                onClick={() => append(defaultExtraValues())}
                                variant="outline"
                                className="col-span-full ml-auto w-auto"
                            >
                                <PiPlusBold className="me-2 h-4 w-4" /> Add Extra
                            </Button>
                        </FormGroup>

                        <FormFooter
                            className="mt-6"
                            isLoading={isLoading}
                            submitBtnText={venue ? "Update Venue" : "Create Venue"}
                        />
                    </form>
                </div>
            </div>
        </div>
    );
}